import { Checkbox, Icon, MenuItem, SelectTextField, Stack, Switch, TextField, Tooltip, Typography } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { Controller, useFormContext } from "react-hook-form"
import { z } from "zod"
import { useArticleFormDefaults } from "@bundles/basket/hooks/Forms/useArticleFormDefaults"
import { useOwnArticlesStore } from "@bundles/compilations/components/compilations/OwnArticles/store"
import { useAllCompilations } from "@bundles/compilations/components/compilations/OwnArticles/useAllCompilations"
import { getBundleParams } from "@bundles/compilations/utils"
import { errorHandling } from "@tm/utils"
import { useEffect } from "react"
import { useCustomPartSchema } from "./hooks/useCustomPartSchema"
import { NewCustomListMenuItem } from "../../_shared/NewCustomListMenuItem"

type Props = {
    onSaveError: (error: string) => void
}

export function OwnArticleFields({ onSaveError }: Props) {
    const { translateText } = useLocalization()
    const { categoryItems } = useArticleFormDefaults()
    const { enableArticleNotes } = getBundleParams()
    const { categoryFilter } = useOwnArticlesStore()
    const compilations = useAllCompilations()
    const schema = useCustomPartSchema()

    const { control, watch, setValue } = useFormContext<z.infer<typeof schema>>()
    const categoryId = Number(watch("categoryId"))
    const saveAsOwnArticle = watch("saveAsOwnArticle")

    useEffect(() => {
        if (!saveAsOwnArticle) {
            return
        }

        // Set default values for categoryId and customArticleListId
        setValue("categoryId", categoryItems[0]?.category?.categoryId)
        setValue("customArticleListId", compilations[0]?.id)
    }, [compilations, categoryItems, saveAsOwnArticle, setValue])

    return (
        <>
            <Stack gap={0.75}>
                <Typography variant="h3">{translateText(757)}</Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Controller
                        control={control}
                        name="saveAsOwnArticle"
                        render={({ field, fieldState }) => (
                            <Switch
                                size="small"
                                label={translateText(14115)}
                                checked={field.value}
                                formControlLabelSx={{ marginRight: 0 }}
                                {...field}
                                {...errorHandling(fieldState)}
                            />
                        )}
                    />
                    <Tooltip title={translateText(14116)} placement="right">
                        <Icon name="info" />
                    </Tooltip>
                </Stack>
            </Stack>
            {saveAsOwnArticle && (
                <>
                    <Stack direction="row" gap={2}>
                        <Controller
                            control={control}
                            name="categoryId"
                            render={({ field, fieldState }) => (
                                <SelectTextField
                                    required
                                    disabled={!!categoryFilter}
                                    label={translateText(158)}
                                    size="extralarge"
                                    maxLayerHeight={300}
                                    sx={{ flex: 1 }}
                                    {...field}
                                    {...errorHandling(fieldState)}
                                >
                                    {categoryItems?.map((cat) => (
                                        <MenuItem key={cat.category.categoryId} value={cat.category.categoryId}>
                                            {cat.text}
                                        </MenuItem>
                                    ))}
                                </SelectTextField>
                            )}
                        />
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <Controller
                                control={control}
                                name="isDefaultForCategory"
                                render={({ field }) => <Checkbox checked={!!field.value} disabled={!categoryId || categoryId === 3060} {...field} />}
                            />
                            <Icon name="category-bestseller" />
                            <Typography variant="label">{translateText(12702)}</Typography>
                            <Tooltip title={translateText(14117)} placement="bottom-start">
                                <Icon name="info" />
                            </Tooltip>
                        </Stack>
                        <Controller
                            control={control}
                            name="customArticleListId"
                            render={({ field, fieldState }) => (
                                <SelectTextField
                                    required
                                    label={translateText(12791)}
                                    size="extralarge"
                                    maxLayerHeight={300}
                                    sx={{ flex: 1 }}
                                    {...field}
                                    {...errorHandling(fieldState)}
                                >
                                    {compilations?.map((compilation) => (
                                        <MenuItem key={compilation.id} value={compilation.id}>
                                            {compilation.name}
                                        </MenuItem>
                                    ))}
                                    <NewCustomListMenuItem variant="partList" hasCustomItems={!!compilations.length} onSaveError={onSaveError} />
                                </SelectTextField>
                            )}
                        />
                    </Stack>
                    {enableArticleNotes && (
                        <Controller
                            control={control}
                            name="note"
                            render={({ field, fieldState }) => (
                                <TextField
                                    size="extralarge"
                                    multiline
                                    label={translateText(39)}
                                    fullWidth
                                    inputCounter
                                    minRows={3}
                                    inputProps={{
                                        maxLength: 500,
                                    }}
                                    {...field}
                                    {...errorHandling(fieldState)}
                                />
                            )}
                        />
                    )}
                </>
            )}
        </>
    )
}
